// Сводное заключение по проекту (мигр. 075).
//
// Четыре машинных прогона и находки взаимоувязки — сырой результат: в нём есть
// и ложные срабатывания, и пробелы, которых движок не видит (отсутствующий в
// поставке раздел не даёт ни одного «не раскрыто»). Здесь лежит выверенная
// человеком картина: что проверено, что подтвердилось, что снято и чего не
// хватает для полной проверки.
//
// Доступ: черновик отдаётся только внутренним ролям (сервер), заказчику
// опубликованное заключение открывается по правилам тарифа — 402 → замок.
import { useQuery } from '@tanstack/react-query';
import { FileCheck2, Lock } from 'lucide-react';
import { api, ApiError } from '@/api/client';

interface Block { heading?: string; text?: string; items?: string[]; note?: string }
interface Conclusion {
  id: string; title: string; verdict_level: 'ready' | 'attention' | 'not_ready';
  verdict_text: string; body_json: Block[]; author: string; status: string;
  created_at: string | null; updated_at: string | null;
}

const LEVEL: Record<string, { color: string; bg: string; border: string; label: string }> = {
  ready:     { color: '#047857', bg: 'rgba(16,185,129,.07)', border: 'rgba(16,185,129,.28)', label: 'Существенных препятствий не найдено' },
  attention: { color: '#B45309', bg: 'rgba(245,158,11,.07)', border: 'rgba(245,158,11,.32)', label: 'Требует внимания перед подачей' },
  not_ready: { color: '#B42318', bg: 'rgba(239,68,68,.06)',  border: 'rgba(239,68,68,.30)',  label: 'Не готова к подаче' },
};

function fmt(d: string | null): string {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch { return ''; }
}

export function ConclusionPanel({ projectId, onPay }: { projectId: string; onPay?: () => void }) {
  const { data, error, isLoading } = useQuery<Conclusion, ApiError>({
    queryKey: ['conclusion', projectId],
    queryFn: () => api.get(`/projects/${projectId}/conclusion`),
    retry: false,
  });

  // 404 — заключения нет (или оно ещё черновик и роль не внутренняя): не рисуем ничего.
  if (isLoading) return null;
  if (error && error.status === 402) {
    return (
      <div className="rounded-card" style={{ padding: '22px 24px', background: '#FFF', border: '1px solid #E5E7EB', marginBottom: 20 }}>
        <div className="flex items-start" style={{ gap: 12 }}>
          <Lock size={20} stroke="#B45309" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <h3 className="font-heading text-ink" style={{ fontSize: 16.5, fontWeight: 650 }}>
              Сводное заключение по проекту готово
            </h3>
            <p className="text-muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginTop: 6, maxWidth: 620 }}>
              Специалист выверил результат машинных проверок: снял ложные срабатывания,
              добавил то, чего проверки не видят, и указал, каких томов не хватает для
              полной проверки. Откроется после оплаты тарифа.
            </p>
            {onPay && (
              <button type="button" onClick={onPay} className="inline-flex items-center rounded-pill"
                style={{ marginTop: 14, gap: 7, padding: '9px 18px', fontSize: 14, fontWeight: 700,
                         background: '#F59E0B', color: '#1F1300', border: 'none', cursor: 'pointer' }}>
                Оплатить, чтобы прочитать
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (error || !data) return null;

  const lv = LEVEL[data.verdict_level] ?? LEVEL.attention;

  return (
    <section className="rounded-card" style={{ padding: '22px 24px', background: lv.bg, border: `1px solid ${lv.border}`, marginBottom: 20 }}>
      <header className="flex items-start" style={{ gap: 12 }}>
        <FileCheck2 size={22} stroke={lv.color} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <div className="flex items-center" style={{ gap: 10, flexWrap: 'wrap' }}>
            <h3 className="font-heading" style={{ fontSize: 17.5, fontWeight: 650, color: lv.color }}>{data.title}</h3>
            {data.status === 'draft' && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                             color: '#475467', background: 'rgba(71,84,103,.10)' }}>
                черновик — заказчику не виден
              </span>
            )}
          </div>
          <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
            {lv.label}{data.author ? ` · ${data.author}` : ''}{data.updated_at ? ` · ${fmt(data.updated_at)}` : ''}
          </div>
          {data.verdict_text && (
            <p className="text-ink" style={{ fontSize: 14, lineHeight: 1.65, marginTop: 10, whiteSpace: 'pre-wrap' }}>
              {data.verdict_text}
            </p>
          )}
        </div>
      </header>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {(data.body_json || []).map((b, i) => (
          <div key={i} style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px' }}>
            {b.heading && (
              <h4 className="font-heading text-ink" style={{ fontSize: 14.5, fontWeight: 650 }}>{b.heading}</h4>
            )}
            {b.text && (
              <p className="text-ink" style={{ fontSize: 13.5, lineHeight: 1.65, marginTop: b.heading ? 7 : 0, whiteSpace: 'pre-wrap' }}>
                {b.text}
              </p>
            )}
            {b.items && b.items.length > 0 && (
              <ul className="text-ink" style={{ fontSize: 13.5, lineHeight: 1.6, marginTop: 9, paddingLeft: 18, listStyle: 'disc' }}>
                {b.items.map((it, j) => <li key={j} style={{ marginTop: j ? 4 : 0 }}>{it}</li>)}
              </ul>
            )}
            {b.note && (
              <p className="text-muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 10,
                                                 paddingTop: 9, borderTop: '1px dashed #E5E7EB' }}>
                {b.note}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
